import React, { FC, useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import dayjs from 'dayjs';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { Download, User, Edit, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useGetReports from '@/hooks/useGetReports';
import useGetProfile from '@/hooks/useGetProfile';
import { useAuth0 } from '@auth0/auth0-react';
import { GetHelpButton } from '@/components/GetHelpButton';
import useUpdateProfile from '@/hooks/useUpdateProfile';
import useCreateReport from '@/hooks/useCreateReport';
import { compact } from 'lodash';
import { useNavigate } from 'react-router-dom';

// Form schema for password change
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Form schema for email update
const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

interface Props {
  tableOnly?: boolean;
}

const MyDashboard: FC<Props> = ({ tableOnly = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const { data: user } = useGetProfile();
  const { toast } = useToast();
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const { data: profile } = useGetProfile();
  const { data: reports, isFetched } = useGetReports();
  const { mutate: updateUser, isPending: updateLoading } = useUpdateProfile();
  const { mutate: createReport } = useCreateReport();

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect({
        appState: {
          returnTo: '/dashboard?tab=reports',
        },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated && profile && !updateLoading) {
      const user = JSON.parse(window.localStorage.getItem('user') || null);
      const userType = window.localStorage.getItem('user_type');
      if (!!profile && (!!user || !!userType)) {
        updateUser({
          firstName: user?.firstName,
          lastName: user?.lastName,
          phoneNumber: user?.phoneNumber,
          role: userType,
        });
      }
    }
  }, [createReport, isAuthenticated, profile, updateLoading, updateUser]);

  useEffect(() => {
    if (isAuthenticated && profile && isFetched) {
      const propertyInfo = JSON.parse(
        window.localStorage.getItem('propertyInfo') || null,
      );

      const comparableProperties = JSON.parse(
        window.localStorage.getItem('comparableProperties') || null,
      );

      const aiProperties = JSON.parse(
        window.localStorage.getItem('aiProperties') || null,
      );

      const fairMarketValue = window.localStorage.getItem('fairMarketValue');

      const officialValuation =
        window.localStorage.getItem('officialValuation');

      const assessorInfo = JSON.parse(
        window.localStorage.getItem('assessorInfo') || null,
      );

      const storageImages = JSON.parse(
        window.localStorage.getItem('subjectPropertyImages') || '[]',
      );

      const subjectPropertyImages = storageImages?.length
        ? [
            ...storageImages.map((i) => i.url),
            ...(propertyInfo?.mls?.Media?.map((img) => img.MediaURL) || []),
          ]
        : propertyInfo?.mls?.Media?.map((img) => img.MediaURL) || [];

      if (comparableProperties && propertyInfo && assessorInfo) {
        createReport({
          name: `${profile?.firstName} ${profile?.lastName}`,
          emailAddress: profile?.emailAddress,
          phoneNumber: profile?.phoneNumber,
          address: compact([
            propertyInfo.address.address,
            propertyInfo.address.county,
            propertyInfo.address.state,
            compact([propertyInfo.address.zip, propertyInfo.address.zip4]).join(
              '-',
            ),
          ]).join(', '),
          comparableSales: comparableProperties.filter(
            (comp) => !aiProperties?.find((ai) => ai.id === comp.id),
          ),
          aiComparableSales: aiProperties,
          apn: comparableProperties?.[0]?.apn || '',
          state: propertyInfo.address.state,
          officialValuation,
          fairMarketValue,
          legalDescription: propertyInfo.legalDescription,
          assessor: {
            address: assessorInfo.address,
            countyName: assessorInfo.name,
            phone: assessorInfo.phone,
            websiteUrl: assessorInfo.websiteUrl,
            protestUrl: assessorInfo.protestUrl,
          },
          images: subjectPropertyImages,
          yearBuilt: propertyInfo.yearBuilt,
        });
      }
    }
  }, [isAuthenticated, profile, isFetched]);

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: user?.emailAddress,
    },
  });

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
    setShowDownloadDialog(true);
  };

  const handlePasswordSubmit = (values) => {
    // This would be a real API call in production
    console.log('Password change:', values);
    toast({
      title: 'Password updated',
      description: 'Your password has been successfully changed.',
    });
    setShowPasswordDialog(false);
    passwordForm.reset();
  };

  const handleEmailSubmit = (values) => {
    // This would be a real API call in production
    console.log('Email update:', values);
    toast({
      title: 'Email updated',
      description: 'Your email has been successfully updated.',
    });
    setShowEmailDialog(false);
  };

  if (tableOnly) {
    return (
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left column - Properties */}
        <div className="flex-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                My Properties
              </CardTitle>
              <CardDescription>Properties you've analyzed</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-x-auto">
                <Table className="min-w-[780px] w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property Address</TableHead>
                      <TableHead>Date Added</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(reports ?? []).map((property) => (
                      <TableRow key={property._id}>
                        <TableCell className="font-medium">
                          {property.address}
                        </TableCell>
                        <TableCell>
                          {dayjs(property.createdAt).format('YYYY-MM-DD')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {(property.status || 'Complete') ===
                              'Processing' && (
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                            )}
                            {property.status || 'Complete'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center self-end"
                            onClick={() =>
                              navigate(`/property-tax-report/${property._id}`)
                            }
                            disabled={property.status === 'Processing'}
                          >
                            Edit Report
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left column - Properties */}
          <div className="flex-1">
            <Card>
              <CardHeader className="flex space-y-0 flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold">
                    My Properties
                  </CardTitle>
                  <CardDescription>Properties you've analyzed</CardDescription>
                </div>
                <GetHelpButton />
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <Table className="min-w-[780px] w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property Address</TableHead>
                        <TableHead>Date Added</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reports ?? []).map((property) => (
                        <TableRow key={property._id}>
                          <TableCell className="font-medium">
                            {property.address}
                          </TableCell>
                          <TableCell>
                            {dayjs(property.createdAt).format('YYYY-MM-DD')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {(property.status || 'Complete') ===
                                'Processing' && (
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              )}
                              {property.status || 'Complete'}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center self-end"
                              onClick={() =>
                                navigate(`/property-tax-report/${property._id}`)
                              }
                              disabled={property.status === 'Processing'}
                            >
                              Edit Report
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column - Account Settings */}
          <div className="w-full md:w-80 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowPasswordDialog(true)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Change Password
                </Button>
                {/* <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowEmailDialog(true)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Update Email
                </Button> */}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="rounded-full bg-tax-lightGray p-3">
                    <User className="h-6 w-6 text-tax-deepBlue" />
                  </div>
                  <div className="max-w-[calc(100%-64px)]">
                    <p className="font-medium truncate max-w-full">
                      {user?.emailAddress}
                    </p>
                    {user?.createdAt && (
                      <p className="text-sm text-muted-foreground">
                        Member since{' '}
                        {dayjs(user?.createdAt).format('MMMM YYYY')}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Download Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Report</DialogTitle>
            <DialogDescription>
              This link will start a download of the final report in DOC format.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowDownloadDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Please enter your current password and a new password.
            </DialogDescription>
          </DialogHeader>
          <Form {...passwordForm}>
            <form
              onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
              className="space-y-4"
            >
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Update Password</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Email Update Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Email</DialogTitle>
            <DialogDescription>
              Please enter your new email address.
            </DialogDescription>
          </DialogHeader>
          <Form {...emailForm}>
            <form
              onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
              className="space-y-4"
            >
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEmailDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Update Email</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MyDashboard;
